/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IPriceComponentResources } from '@msdyn365-commerce/components';
import MsDyn365, { IAppSettings, ICoreContext } from '@msdyn365-commerce/core';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import {
    ICategoryHierarchyViewProps,
    IRefineMenuViewProps,
    ISearchResultContainerViewProps,
    ISearchResultModalViewProps,
    ISortByViewProps,
    ITitleViewProps
} from '@msdyn365-commerce-modules/search-result-container';
import { Button, getTelemetryObject, Module, Node } from '@msdyn365-commerce-modules/utilities';
import React, { MouseEventHandler, useEffect, useState } from 'react';

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import {
    ISearchResultContainerProps,
    ISearchResultContainerResources
} from '../definition-extensions/search-result-container.ext.props.autogenerated';
import { ProductComponent } from './components/product.component';

import classNames from 'classnames';

import { getParameter, isYes } from '../../../common/utilities/global-parameters.utils';

import {
    RootCategoryNames,
    RootCategoryName,
    isValidRootCategory,
    isProductsCategory,
    isRecipesCategory
} from '../../../common/config/category-constants';

import { checkIsAccessible, isProductVisibleForCustomer } from '../../../common/utilities/access-control';

import { getAttributeByTechnicalName } from '../../../common/utilities/attributes-helpers';
// MobXDebug utilities
// import { debugMobXProducts, extractProductsFromMobX } from '../../../common/utilities/mobx-debug.utils';

import { getCurrentAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import { getCustomer, GetCustomerInput } from '@msdyn365-commerce-modules/retail-actions';
// import { checkVolumePricingValidity } from '../../../common/utilities/volume-pricing.utils';

/**
 * DescriptionInterval.
 */
enum DescriptionInterval {
    productDescriptionInterval = 14,
    productDescriptionIntervalRemainder0 = 0,
    productDescriptionIntervalRemainder9 = 9
}

/**
 * Interface pour un refiner enrichi avec son nom technique
 */
interface IRefinerWithTechnicalName {
    refinerComponent: React.ReactNode;
    technicalName: string;
}

/**
 * Extrait les données des composants RefineSubmenu pour identifier les noms techniques
 */
const extractRefinerData = (refiners: React.ReactNode[], appSettings: IAppSettings): IRefinerWithTechnicalName[] => {
    const refinerData: IRefinerWithTechnicalName[] = [];

    refiners.forEach(refiner => {
        if (React.isValidElement(refiner) && refiner.props) {
            const props = refiner.props as any;
            const productRefinerHierarchy = props.productRefinerHierarchy;

            if (productRefinerHierarchy) {
                const recordId = productRefinerHierarchy.RecordId;
                const technicalName = recordId ? getParameter(recordId.toString(), appSettings) || '' : '';

                refinerData.push({
                    refinerComponent: refiner,
                    technicalName
                });
            }
        }
    });

    return refinerData;
};

/**
 * Traite les refiners pour regrouper les WBS_SN_ tout en gardant les autres intacts (avec possibilité d'eclusion de filtres)
 */
const processRefinersWithSpecialNeedsGrouping = (
    titleSpecialNeedsFilter: string,
    refiners: React.ReactNode[],
    appSettings: IAppSettings
): React.ReactNode[] => {
    if (!refiners || refiners.length === 0) {
        return refiners;
    }

    // Liste des noms techniques à exclure du DOM
    const excludedTechnicalNames = ['N/A']; //['WBS_NEW_ITEM', 'WBS_VOLUME_PRICING'];

    const refinerData = extractRefinerData(refiners, appSettings);
    const processedRefiners: React.ReactNode[] = [];

    // Séparer les WBS_SN_ des autres
    const specialNeedsRefiners = refinerData.filter(item => item.technicalName.startsWith('WBS_SN_'));

    const otherRefiners = refinerData.filter(
        item => !item.technicalName.startsWith('WBS_SN_') && !excludedTechnicalNames.includes(item.technicalName)
    );

    // Ajouter les autres refiners sans modification
    otherRefiners.forEach(item => {
        processedRefiners.push(item.refinerComponent);
    });

    // Si on a des refiners WBS_SN_, les regrouper
    if (specialNeedsRefiners.length > 0) {
        // Créer le groupe "Besoins Spéciaux"
        const specialNeedsGroup = (
            <div key='special-needs-group' className='ms-refine-submenu list-group refiner-group--special-needs'>
                <button
                    type='button'
                    aria-label='Besoins Spéciaux'
                    aria-expanded='false'
                    className='ms-refine-submenu__toggle_collapsed msc-btn'
                >
                    {titleSpecialNeedsFilter}
                </button>
                <div
                    className='refiner-group__content'
                    ref={element => {
                        if (element && MsDyn365.isBrowser) {
                            // Utiliser setTimeout pour s'assurer que le DOM est entièrement rendu
                            setTimeout(() => {
                                // Cibler tous les spans avec la classe ms-refine-submenu-item__label
                                const labelSpans = element.querySelectorAll('.ms-refine-submenu-item__label');

                                labelSpans.forEach(span => {
                                    const textContent = span.textContent?.trim() || '';

                                    // Vérifier si le texte commence par "No" suivi d'un espace et des parenthèses
                                    if (textContent.match(/^No\s*\(/)) {
                                        // Trouver le li parent et le masquer
                                        const listItem = span.closest('li.ms-refine-submenu-item');
                                        if (listItem) {
                                            listItem.classList.add('hidden-no-option');
                                        }
                                    }
                                });
                            }, 200); // Délai légèrement plus long pour être sûr
                        }
                    }}
                >
                    {specialNeedsRefiners.map((item, index) => (
                        <React.Fragment key={`special-needs-${index}`}>{item.refinerComponent}</React.Fragment>
                    ))}
                </div>
            </div>
        );

        processedRefiners.push(specialNeedsGroup);
    }

    // Debug : afficher les filtres exclus
    // const excludedRefiners = refinerData.filter(item => excludedTechnicalNames.includes(item.technicalName));
    // if (excludedRefiners.length > 0) {
    //     console.log(
    //         '🚫 Filtres exclus du DOM:',
    //         excludedRefiners.map(item => item.technicalName)
    //     );
    // }

    return processedRefiners;
};

/** Filtre les produits en fonction de l'attribut WBS_CUST_HIERARCHY_SPECIFIC et du businessPartnerId
 */
function filterVisibleProducts(
    products: ProductSearchResult[],
    appSettings: IAppSettings,
    businessPartnerId?: string
): ProductSearchResult[] {
    // console.log('🔍 Filtrage des produits avec businessPartnerId:', businessPartnerId);
    // console.log('📦 products:', products);
    return products.filter(product => {
        const wbsCustomerSpecificAttribute = getAttributeByTechnicalName(
            product.AttributeValues,
            'WBS_CUST_HIERARCHY_SPECIFIC',
            appSettings
        );
        const wbsCustomerSpecific = wbsCustomerSpecificAttribute?.attribute.TextValue;
        // if (wbsCustomerSpecific) {
        //     console.log(
        //         '🆔 Product RecordId:',
        //         product.RecordId,
        //         ' - Product Name:',
        //         product.Name,
        //         ' - WBS_CUST_HIERARCHY_SPECIFIC:',
        //         wbsCustomerSpecific
        //     );
        // }
        return isProductVisibleForCustomer(wbsCustomerSpecific, businessPartnerId);
    });
}

/**
 * Composant personnalisé pour rendre les produits avec contrôle d'accès
 */
const CustomProductSearchResultItems: React.FC<{
    products: ProductSearchResult[];
    context: ICoreContext;
    resources: ISearchResultContainerResources;
    imageSettings?: any;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: any;
    quickviewSlot?: any;
    productComparisonButton?: any;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: any;
    currentRootCategory: string;
    isAuthenticated: boolean;
    newItemLabel: string;
    productSignInLabel: string;
    businessPartnerId?: string;
    customer?: any;
}> = ({
    products,
    context,
    resources,
    imageSettings,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    productComparisonButton,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    currentRootCategory,
    isAuthenticated,
    newItemLabel,
    productSignInLabel,
    businessPartnerId,
    customer
}) => {
    const appSettings = context.app as IAppSettings;
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    return (
        <ul className='list-unstyled'>
            {products.map((product: ProductSearchResult, index: number) => {
                /**** Verrou d'accès ****/
                // Récupérer l'attribut WBS_CULINA_PREP_DISPLAY pour ce produit
                const wbsCulinaryDisplayAttribute = getAttributeByTechnicalName(
                    product.AttributeValues,
                    'WBS_CULINA_PREP_DISPLAY',
                    appSettings
                );
                const wbsCulinaryDisplay = wbsCulinaryDisplayAttribute?.attribute.TextValue;
                // Déterminer l'accessibilité pour ce produit
                const isAccessible = checkIsAccessible(currentRootCategory, isAuthenticated, wbsCulinaryDisplay);
                // console.log('🔍 currentRootCategory:', currentRootCategory);
                // console.log('🔍 isAuthenticated:', isAuthenticated);
                // console.log('🔍 wbsCulinaryDisplay:', wbsCulinaryDisplay);
                // console.log('🔐 Is accessible:', isAccessible + ' (' + (product.Name ?? '') + ')');

                // Ajouter la classe guest-only si pas accessible
                const guestClass = isAccessible ? '' : 'guest-only';
                //console.log('🔖 class guestClass:', guestClass);

                /**** Item Name (text) ****/
                const wbsItemNameAttribute = getAttributeByTechnicalName(product.AttributeValues, 'WBS_ITEM_NAME', appSettings);
                const wbsItemName = wbsItemNameAttribute?.attribute.TextValue;
                // console.log('🆔 Product RecordId:', product.RecordId, ' - WBS_ITEM_NAME:', wbsItemName);

                // Sales Unit (text)
                const wbsSalesUnitAttribute = getAttributeByTechnicalName(product.AttributeValues, 'WBS_SALES_UNIT', appSettings);
                const wbsSalesUnit = wbsSalesUnitAttribute?.attribute.TextValue;

                /**** Volume Pricing (text : Yes or No) ****/
                const wbsVolumePricingAttribute = getAttributeByTechnicalName(product.AttributeValues, 'WBS_VOLUME_PRICING', appSettings);
                const wbsVolumePricing: boolean = isYes(wbsVolumePricingAttribute?.attribute.TextValue);
                const wbsVolumePricingClass = wbsVolumePricing ? 'volume-pricing' : '';

                /**** Volume Pricing Table & Validity Check ****/
                // const wbsVolumePricingTableAttribute = getAttributeByTechnicalName(
                //     product.AttributeValues,
                //     'WBS_VOLUME_PRICING_TABLE',
                //     appSettings
                // );
                // const wbsVolumePricingTableJson = wbsVolumePricingTableAttribute?.attribute.TextValue;

                // Vérifier la validité du tableau de prix dégressifs
                // const wbsVolumePricing: boolean = checkVolumePricingValidity(
                //     wbsVolumePricingTableJson,
                //     businessPartnerId,
                //     customer?.PriceGroup
                // );
                // const wbsVolumePricingClass = wbsVolumePricing ? 'volume-pricing' : '';
                // if (product.RecordId == 5637150846) {
                //     console.log(
                //         '🆔 Product RecordId:',
                //         product.RecordId,
                //         ' - WBS_ITEM_NAME:',
                //         wbsItemName,
                //         ' - wbsVolumePricing: ',
                //         wbsVolumePricing,
                //         ' - businessPartnerId:',
                //         businessPartnerId,
                //         ' - customer?.PriceGroup:',
                //         customer?.PriceGroup,
                //         ' - WBS_VOLUME_PRICING_TABLE:',
                //         wbsVolumePricingTableJson,
                //         ' - product.AttributeValues:',
                //         product.AttributeValues
                //     );
                // }

                /**** New item flag (text : Yes or No) ****/
                const wbsNewItemAttribute = getAttributeByTechnicalName(product.AttributeValues, 'WBS_NEW_ITEM', appSettings);
                const wbsNewItem: boolean = isYes(wbsNewItemAttribute?.attribute.TextValue);
                const wbsNewItemClass = wbsNewItem ? 'new-item' : '';

                /**** Recipe Name (text) ****/
                const wbsRecipeNameAttribute = getAttributeByTechnicalName(product.AttributeValues, 'WBS_CULINA_PREP_NAME', appSettings);
                const wbsRecipeName = wbsRecipeNameAttribute?.attribute.TextValue;

                /**** Customer Specific product (text) ****/
                const wbsCustomerSpecificAttribute = getAttributeByTechnicalName(
                    product.AttributeValues,
                    'WBS_CUST_HIERARCHY_SPECIFIC',
                    appSettings
                );
                const wbsCustomerSpecific = wbsCustomerSpecificAttribute?.attribute.TextValue;
                // console.log('🆔 Product RecordId:', product.RecordId, ' - Product Name:', wbsItemName, '/', product.Name , ' - WBS_CUST_HIERARCHY_SPECIFIC:', wbsCustomerSpecific);

                const isVisibleForCustomer = isProductVisibleForCustomer(wbsCustomerSpecific, businessPartnerId);
                // console.log('🔐 isVisibleForCustomer:', isVisibleForCustomer);
                // console.log('🔐 businessPartnerId:', businessPartnerId);

                /**** Final class names : guest-only / volume-pricing / new-item ****/
                // const itemClass = `ms-product-search-result__item ${guestClass} ${wbsVolumePricingClass} ${wbsNewItemClass}`.trim();
                const itemClass = classNames('ms-product-search-result__item', guestClass, wbsVolumePricingClass, wbsNewItemClass);
                //console.log('🔖 classNames:', itemClass);

                const displayName = isProductsCategory(currentRootCategory)
                    ? wbsItemName
                    : isRecipesCategory(currentRootCategory)
                    ? wbsRecipeName
                    : undefined;

                if (
                    // Filtrage de sécurité de robustesse mais éventuellement redondant avec filterVisibleProducts
                    isVisibleForCustomer
                ) {
                    return (
                        <li className={itemClass} key={index}>
                            {wbsNewItem ? <div className='new-item-label'>{newItemLabel}</div> : null}
                            <ProductComponent
                                context={context}
                                telemetryContent={telemetryContent}
                                imageSettings={imageSettings}
                                freePriceText={resources.priceOnRequest}
                                originalPriceText={resources.originalPriceText}
                                currentPriceText={resources.currentPriceText}
                                ratingAriaLabel={resources.ratingAriaLabel}
                                allowBack={allowBackNavigation}
                                id={moduleId}
                                key={product.RecordId}
                                typeName={moduleType}
                                data={{ product }}
                                quickViewButton={quickviewSlot}
                                productComparisonButton={productComparisonButton}
                                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                                isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                                priceResources={priceResources}
                                swatchItemAriaLabel={resources.swatchItemAriaLabel}
                                dimensionAvailabilities={productsDimensionAvailabilities?.find(
                                    (dimensionAvailability: IProductsDimensionsAvailabilities[]) =>
                                        ArrayExtensions.hasElements(dimensionAvailability) &&
                                        dimensionAvailability[0].masterProductId === product.MasterProductId
                                )}
                                currentRootCategory={currentRootCategory}
                                isAccessible={isAccessible}
                                displayName={displayName}
                                productSignInLabel={productSignInLabel}
                                wbsSalesUnit={wbsSalesUnit}
                            />
                        </li>
                    );
                }
                return <></>;
            })}
        </ul>
    );
};

/**
 * Render Clear button for mobile viewport.
 * @param productSignInLabel - The label for sign-in for guest-only.
 * @param currentRootCategory - The current root category.
 * @param isAuthenticated - The authentication status.=
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProductCards = (
    productSignInLabel: string,
    currentRootCategory: string,
    isAuthenticated: boolean,
    props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>,
    businessPartnerId: string | undefined,
    customer: any
): JSX.Element | null => {
    // const productsResult = props.data.products?.result;

    // Utilisation de l'utilitaire de debug
    // debugMobXProducts(productsResult, {
    //     context: 'Search Result Products',
    //     showArrayContent: true,
    //     maxArrayItems: 2,
    //     logPrefix: '🛍️'
    // });

    // Extraction simple des produits
    // const products = extractProductsFromMobX<ProductSearchResult>(productsResult);

    // if (products.length > 0) {
    //     console.log('✅ Produits extraits avec succès:', products.length);
    //     products.forEach((product: ProductSearchResult, index: number) => {
    //         console.log(`Produit ${index} (${product.ItemId}) - AttributeValues:`, product?.AttributeValues);
    //     });
    // }

    // return (
    //     <Node {...props.ProductsContainer}>
    //         <div className={guestClass}>{props.products}</div>
    //     </Node>
    // );

    // Le contrôleur a déjà fait la pagination/filtrage dans listPageState.activeProducts
    const paginatedProducts = props.data.listPageState.result?.activeProducts || [];
    const visibleProducts = filterVisibleProducts(paginatedProducts, props.context.app as IAppSettings, businessPartnerId);
    // console.log('📦 Produits paginés par le contrôleur:', paginatedProducts.length);

    // Créer telemetryContent
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);

    // Déterminer allowBack
    const breadCrumbType = props.context.app.config.breadcrumbType;
    const isAllowBack = props.config.allowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');

    return (
        <Node {...props.ProductsContainer}>
            <div className='wrapper-authentication'>
                <CustomProductSearchResultItems
                    products={visibleProducts}
                    context={props.context}
                    resources={props.resources}
                    imageSettings={props.config?.imageSettings}
                    moduleType={props.typeName}
                    moduleId={props.id}
                    allowBackNavigation={isAllowBack}
                    telemetryContent={telemetryContent}
                    quickviewSlot={ArrayExtensions.hasElements(props.slots?.quickview) ? props.slots.quickview[0] : undefined}
                    productComparisonButton={
                        ArrayExtensions.hasElements(props.slots?.productComparisonButton)
                            ? props.slots.productComparisonButton[0]
                            : undefined
                    }
                    channelInventoryConfigurationId={props.data.products.result?.channelInventoryConfigurationId}
                    isPriceMinMaxEnabled={
                        props.data.featureState?.result?.find(
                            (featureState: any) => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
                        )?.IsEnabled
                    }
                    productsDimensionAvailabilities={props.productsDimensionAvailabilities}
                    currentRootCategory={currentRootCategory}
                    isAuthenticated={isAuthenticated}
                    newItemLabel={props.config.newItemLabel || 'New'}
                    productSignInLabel={productSignInLabel}
                    businessPartnerId={businessPartnerId}
                    customer={customer}
                />
            </div>
        </Node>
    );
};

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

/**
 * Render Clear button for mobile viewport.
 * @param props - The view props.
 * @param product - The product item data.
 * @param index - The index of each product item.
 * @param isEnabledProductDescription - The flag for showing Product desription tile.
 * @returns The JSX Element.
 */
const renderProductCardsDescription = (
    props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>,
    product: ProductSearchResult,
    index: number,
    isEnabledProductDescription?: boolean,
    businessPartnerId?: string
): JSX.Element | null => {
    const { config, context, resources, productsDimensionAvailabilities } = props;
    const telemetryContent = getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    const breadCrumbType = props.context.app.config.breadcrumbType;
    const isAllowBack = props.config.allowBackNavigation && (breadCrumbType === 'back' || breadCrumbType === 'categoryAndBack');
    const classname = isEnabledProductDescription
        ? 'ms-product-search-result__item product__description'
        : 'ms-product-search-result__item';
    const quickviewslot = ArrayExtensions.hasElements(props.slots.quickview) ? props.slots.quickview[0] : undefined;
    const channelInventoryConfigurationId = props.data.products.result?.channelInventoryConfigurationId;
    const isPriceMinMaxFeatureState = props.data.featureState.result?.find(
        featureState => featureState.Name === 'Dynamics.AX.Application.RetailSearchPriceRangeFeature'
    );
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const productComparisonButton = ArrayExtensions.hasElements(props.slots.productComparisonButton)
        ? props.slots.productComparisonButton[0]
        : null;

    return (
        <li className={classname} key={index}>
            <ProductComponent
                context={context}
                telemetryContent={telemetryContent}
                imageSettings={config.imageSettings}
                freePriceText={resources.priceOnRequest}
                originalPriceText={resources.originalPriceText}
                currentPriceText={resources.currentPriceText}
                ratingAriaLabel={resources.ratingAriaLabel}
                allowBack={isAllowBack}
                id={props.id}
                key={product.RecordId}
                typeName={props.typeName}
                data={{ product }}
                quickViewButton={quickviewslot}
                productComparisonButton={productComparisonButton}
                isEnabledProductDescription={isEnabledProductDescription}
                inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                isPriceMinMaxEnabled={isPriceMinMaxFeatureState?.IsEnabled}
                priceResources={priceResources}
                swatchItemAriaLabel={resources.swatchItemAriaLabel}
                dimensionAvailabilities={productsDimensionAvailabilities?.find(
                    (dimensionAvailability: IProductsDimensionsAvailabilities[]) =>
                        ArrayExtensions.hasElements(dimensionAvailability) &&
                        dimensionAvailability[0].masterProductId === product.MasterProductId
                )}
            />
        </li>
    );
};

/**
 * Render Clear button for mobile viewport.
 * @param productSignInLabel - The label for sign-in for guest-only.
 * @param currentRootCategory - The current root category.
 * @param isAuthenticated - The authentication status.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderProducts = (
    productSignInLabel: string = 'Achetez',
    currentRootCategory: string = RootCategoryNames.PRODUCTS,
    isAuthenticated: boolean = false,
    props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>,
    businessPartnerId: string | undefined,
    customer: any
): JSX.Element | null => {
    const products = props.data.listPageState.result?.activeProducts;
    if (!products || !ArrayExtensions.hasElements(products)) {
        return null;
    }
    const visibleProducts = filterVisibleProducts(products, props.context.app as IAppSettings, businessPartnerId);
    const productDescriptionInterval: number = DescriptionInterval.productDescriptionInterval;
    const productDescriptionIntervalRemainder0: number = DescriptionInterval.productDescriptionIntervalRemainder0;
    const productDescriptionIntervalRemainder9: number = DescriptionInterval.productDescriptionIntervalRemainder9;
    return (
        <Node {...props.ProductsContainer}>
            {props.errorMessage}
            <ul className='list-unstyled'>
                {visibleProducts.map((product: ProductSearchResult, index: number) =>
                    index % productDescriptionInterval === productDescriptionIntervalRemainder0 ||
                    index % productDescriptionInterval === productDescriptionIntervalRemainder9
                        ? renderProductCardsDescription(props, product, index, true, businessPartnerId)
                        : renderProductCardsDescription(props, product, index, false, businessPartnerId)
                )}
            </ul>
        </Node>
    );
};

/**
 * Render Title Count.
 * @param props - The title view props.
 * @param currentRootCategory - The current root category.
 * @returns The JSX Element.
 */
const renderTitleCount = (props: ITitleViewProps, currentRootCategory: string): JSX.Element | null => {
    const { title } = props;
    if (!title) {
        return null;
    }

    // console.log('🔖 renderTitleCount - currentRootCategory:', currentRootCategory);

    const adaptCountText = (original: React.ReactNode): React.ReactNode => {
        // Only adapt if we are in a recipes root category
        const currentCategoryToUpper = currentRootCategory.toUpperCase();

        const isRecipesRoot =
            currentCategoryToUpper === RootCategoryNames.RECIPES ||
            currentCategoryToUpper === RootCategoryNames.RECIPES_FR ||
            currentCategoryToUpper === RootCategoryNames.RECIPES_NL;

        // console.log('🔖 isRecipesRoot:', isRecipesRoot);

        if (!isRecipesRoot) {
            return original;
        }

        // Extraction robuste du texte depuis <Title text={...}/> ou autres éléments
        const extractText = (node: React.ReactNode): string | undefined => {
            if (node === null || node === undefined) return undefined;
            if (typeof node === 'string' || typeof node === 'number') return String(node);
            if (Array.isArray(node)) {
                // Concaténer les fragments de texte trouvés
                const parts = node.map(child => extractText(child)).filter((p): p is string => !!p);
                return parts.length ? parts.join(' ') : undefined;
            }
            if (React.isValidElement(node)) {
                const propsAny = node.props as any;
                // Cas spécifique du composant <Title text="...">
                if (propsAny && (typeof propsAny.text === 'string' || typeof propsAny.text === 'number')) {
                    return String(propsAny.text);
                }
                // Sinon essayer les children
                if (propsAny && propsAny.children) {
                    return extractText(propsAny.children);
                }
            }
            return undefined;
        };

        const text = extractText(original);
        // console.log('🔖 extractText:', text);

        if (!text) {
            return original;
        }

        // Match patterns like "12 products", "1 produit", "3 producten", etc.
        const match = text.match(/^(\d+)\s*(product(en|s)?|produit(s)?|producten)$/i);
        if (!match) {
            return original;
        }

        const count = parseInt(match[1], 10);
        const isSingular = count === 1;

        let term = '';
        switch (currentCategoryToUpper as RootCategoryName) {
            case RootCategoryNames.RECIPES:
                term = isSingular ? 'recipe' : 'recipes';
                break;
            case RootCategoryNames.RECIPES_FR:
                term = isSingular ? 'recette' : 'recettes';
                break;
            case RootCategoryNames.RECIPES_NL:
                term = isSingular ? 'recept' : 'recepten';
                break;
            default:
                return original;
        }

        return `${count} ${term}`;
    };

    const adapted = adaptCountText(title.titleCount);

    return (
        <div className='ms-search-result-container__title result-count-category'>
            <span className='ms-search-result__collection-title-count'>{adapted}</span>
        </div>
    );

    // if (title) {
    //     return (
    //         <>
    //             <div className='ms-search-result-container__title result-count-category'>
    //                 <span className='ms-search-result__collection-title-count'>
    //                     {title.titleCount}
    //                 </span>
    //             </div>
    //         </>
    //     );
    //     // return <Node {...TitleContainer}>{title.titleCount}</Node>;
    // }
    // return null;
};

/**
 * Render Category Hierarchy.
 * @param props - The category hierarchy view props.
 * @returns The JSX Element.
 */
const renderCategoryHierarchy = (props: ICategoryHierarchyViewProps): JSX.Element | null => {
    const { CategoryHierarchyContainer, categoryHierarchyList, categoryHierarchySeparator } = props;
    if (categoryHierarchyList) {
        return (
            <Node {...CategoryHierarchyContainer}>
                {categoryHierarchyList.map((category, index) => (
                    <React.Fragment key={index}>
                        {category}
                        {categoryHierarchyList[index + 1] && categoryHierarchySeparator}
                    </React.Fragment>
                ))}
            </Node>
        );
    }

    return null;
};

/**
 * Render Category Banner.
 * @param props - The view props.
 * @returns The JSX Element.
 */
const renderCategoryBanner = (props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>): JSX.Element | null => {
    // console.log('=== DEBUG renderCategoryBanner ===');
    // console.log('categoryHierarchy:', props.categoryHierarchy);
    // console.log('categoryHierarchy.categoryHierarchyList:', props.categoryHierarchy?.categoryHierarchyList);
    // console.log('data.category:', props.data.category);
    // console.log('data.category.result:', props.data.category?.result);
    // console.log('data.categoryHierarchy:', props.data.categoryHierarchy);
    // console.log('data.categoryHierarchy.result:', props.data.categoryHierarchy?.result);

    // Vérifier si nous avons des données de catégorie
    if (!props.data.category?.result && !props.data.categoryHierarchy?.result) {
        // console.log('❌ Aucune donnée de catégorie trouvée');
        return null;
    }

    // Récupérer la catégorie actuelle (celle affichée)
    const currentCategory = props.data.category?.result;
    //console.log('currentCategory:', currentCategory);

    // Si pas de catégorie actuelle, prendre la première de la hiérarchie
    const categoryHierarchy = props.data.categoryHierarchy?.result;
    const topLevelCategory = currentCategory || (categoryHierarchy && categoryHierarchy[0]);

    if (!topLevelCategory) {
        // console.log('❌ Impossible de déterminer la catégorie à afficher');
        return null;
    }

    const categoryImageUrl = topLevelCategory.Images?.[0]?.Uri;
    const categoryName = topLevelCategory.Name || 'Catégorie';

    // console.log('✅ Banner will render:');
    // console.log('topLevelCategory:', topLevelCategory);
    // console.log('categoryImageUrl:', categoryImageUrl);
    // console.log('categoryName:', categoryName);

    const bannerStyle: React.CSSProperties = {
        backgroundColor: '#ffffff' // Couleur de fond par défaut
    };

    if (categoryImageUrl) {
        bannerStyle.backgroundImage = `url("${categoryImageUrl}")`;
    }

    // const overlayStyle: React.CSSProperties = {
    //     position: 'absolute',
    //     top: 0,
    //     left: 0,
    //     right: 0,
    //     bottom: 0,
    //     backgroundColor: categoryImageUrl ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.1)',
    //     zIndex: 1
    // };

    return (
        <div className='wrapper-msc-category-banner' style={bannerStyle}>
            <div className='msc-category-banner container'>
                {/* <div style={overlayStyle}></div> */}
                <div className='dyn-products-banner-overlay'></div>
                <h1 className='dyn-products-banner-title'>{categoryName}</h1>
            </div>
        </div>
    );
};

/**
 * Function to make filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
export const configureStickyFilter = (isMobile: boolean, isStickyHeader: boolean): void => {
    // Get heights of cookie and promotion banners
    const defaultValue = 0;
    const defaultOffset = 28;
    const headerContainer: HTMLElement | null = document.querySelector('.ms-header');

    const headerHeight: number | undefined = headerContainer?.offsetHeight;
    const containerHeight: number = headerHeight ?? defaultOffset;
    const bannerHeights = isStickyHeader ? defaultValue : containerHeight;
    const headerElement = document.querySelector('header .lock-opaque');
    const defaultContainer = document.querySelector('header .default-container');
    const refinerElement = isMobile ? document.querySelector('.msc-sort-filter-wrapper') : document.querySelector('.ms-refiner-heading');
    if (refinerElement && headerElement && defaultContainer) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-Filter');
            refinerElement.setAttribute('style', `top: ${defaultContainer.clientHeight}px`);
        } else {
            refinerElement.classList.remove('lock-Filter');
        }
    } else if (refinerElement) {
        if (MsDyn365.isBrowser && document.documentElement.scrollTop > bannerHeights) {
            refinerElement.classList.add('lock-top');
        } else {
            refinerElement.classList.remove('lock-top');
            refinerElement.classList.remove('lock-Filter');
            refinerElement.removeAttribute('style');
        }
    }
};

/**
 * Function to setup filter sticky.
 * @param isMobile - The boolean prop.
 * @param isStickyHeader - The boolean prop.
 */
function stickyFilterSetup(isMobile: boolean, isStickyHeader: boolean): void {
    window.addEventListener('scroll', () => {
        configureStickyFilter(isMobile, isStickyHeader);
    });
    configureStickyFilter(isMobile, isStickyHeader);
}

/**
 * Function to make filter sticky.
 * @param refinerRefernce - Ref prop for filter.
 * @param toggle - Toggle function for filter menu.
 */
function useOutsideClick(refinerRefernce: React.RefObject<HTMLElement>, toggle: (event: Event) => void) {
    React.useEffect(() => {
        /**
         * Function to make filter sticky.
         * @param event - Event prop for filter.
         */
        function handleClickOutside(event: Event) {
            const element = document.querySelector('.ms-refine-filter__toggle_collapsed');
            if (refinerRefernce.current && !refinerRefernce.current.contains(event.target as Node) && !element) {
                toggle(event);
            }
        }
        if (MsDyn365.isBrowser) {
            document.addEventListener('click', handleClickOutside);
        }
        return () => {
            if (MsDyn365.isBrowser) {
                document.removeEventListener('click', handleClickOutside);
            }
        };
    }, [refinerRefernce, toggle]);
}

/**
 * Détermine si la catégorie actuelle appartient à la root category "Products" ou "Recipes"
 * @param props - Les props de la vue
 * @returns La root category ou undefined
 */
const getCurrentRootCategory = (props: ISearchResultContainerViewProps & ISearchResultContainerProps<{}>): RootCategoryName | undefined => {
    // 1. Essayer de récupérer depuis la catégorie actuelle
    const currentCategory = props.data.category?.result;

    if (currentCategory?.Name && isValidRootCategory(currentCategory.Name)) {
        // console.log('🏷️ Catégorie actuelle valide dans méthode 1 props.data.category?.result :', currentCategory.Name);
        return currentCategory.Name;
    }

    // 2. Essayer de déduire depuis la hiérarchie
    const categoryHierarchy = props.data.categoryHierarchy?.result;

    if (categoryHierarchy && categoryHierarchy.length > 0) {
        // Chercher dans la hiérarchie
        const rootCategory = categoryHierarchy.find(cat => cat.Name && isValidRootCategory(cat.Name));

        if (rootCategory?.Name && isValidRootCategory(rootCategory.Name)) {
            // console.log('🏷️ Catégorie racine trouvée dans la hiérarchie méthode 2 :', rootCategory.Name);
            return rootCategory.Name;
        }
    }

    // 3. Fallback : analyser l'URL
    const requestUrl = props.context.request.url.requestUrl.pathname.toLowerCase();
    const fullUrl = props.context.request.url.requestUrl.href;

    if (requestUrl.includes('/products') || (requestUrl.includes('/search') && fullUrl.includes('Products'))) {
        // console.log("🏷️ Catégorie racine trouvée dans l'URL méthode 3 :", RootCategoryNames.PRODUCTS);
        return RootCategoryNames.PRODUCTS;
    }

    if (requestUrl.includes('/produits') || (requestUrl.includes('/search') && fullUrl.includes('Produits'))) {
        // console.log("🏷️ Catégorie racine trouvée dans l'URL méthode 3 :", RootCategoryNames.PRODUCTS_FR);
        return RootCategoryNames.PRODUCTS_FR;
    }

    if (requestUrl.includes('/producten') || (requestUrl.includes('/search') && fullUrl.includes('Producten'))) {
        // console.log("🏷️ Catégorie racine trouvée dans l'URL méthode 3 :", RootCategoryNames.PRODUCTS_NL);
        return RootCategoryNames.PRODUCTS_NL;
    }

    if (requestUrl.includes('/recipes') || (requestUrl.includes('/search') && fullUrl.includes('Recipes'))) {
        // console.log("🏷️ Catégorie racine trouvée dans l'URL méthode 3 :", RootCategoryNames.RECIPES);
        return RootCategoryNames.RECIPES;
    }

    if (requestUrl.includes('/recettes') || (requestUrl.includes('/search') && fullUrl.includes('Recettes'))) {
        // console.log("🏷️ Catégorie racine trouvée dans l'URL méthode 3 :", RootCategoryNames.RECIPES_FR);
        return RootCategoryNames.RECIPES_FR;
    }

    if (requestUrl.includes('/recepten') || (requestUrl.includes('/search') && fullUrl.includes('Recepten'))) {
        // console.log("🏷️ Catégorie racine trouvée dans l'URL méthode 3 :", RootCategoryNames.RECIPES_NL);
        return RootCategoryNames.RECIPES_NL;
    }

    return undefined;
};

/**
 * Récupère l'ID de l'organisation (BusinessPartnerId) de l'utilisateur courant.
 */
const getBusinessPartnerId = async (actionContext: any): Promise<string | undefined> => {
    const businessPartner = await getCurrentAsync({ callerContext: actionContext });
    return businessPartner?.BusinessPartnerId;
};

/**
 * Render the category page items.
 * @param props - The view props.collection-title-count
 * @returns The search Result container module view.
 */
const SearchResultContainerView: React.FC<ISearchResultContainerViewProps & ISearchResultContainerProps<{}>> = props => {
    const {
        SearchResultContainer,
        pagination,
        ProductSectionContainer,
        choiceSummary,
        isMobile,
        modalToggle,
        searchResultModal,
        TitleViewProps,
        refineMenu,
        categoryHierarchy,
        sortByOptions,
        CategoryNavContainer,
        RefineAndProductSectionContainer,
        FeatureSearchContainer,
        similarLookProduct,
        errorMessage,
        resources
    } = props;
    useEffect(() => {
        if (props.config.useStickyFilter && MsDyn365.isBrowser) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app context is generic
            stickyFilterSetup(props.isMobile, props.context.app.config.enableStickyHeader as boolean);
        }
    }, [props]);
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const toggle = () => {
        setIsExpanded(!isExpanded);
    };
    const wrapperRefiner = React.useRef(null);
    useOutsideClick(wrapperRefiner, toggle);
    const isRecoSearchPage = props.context.actionContext.requestContext.query?.recommendation;

    // const catalogId = getCatalogId(props.context.request);
    // console.log('🏷️ Current catalogId:', catalogId);

    const appSettings = props.context.app as IAppSettings;

    // Recherche de la rootCategory
    const currentRootCategory = getCurrentRootCategory(props) || '';
    const isAuthenticated = props.context.request.user.isAuthenticated;

    // console.log('🏷️ isAuthenticated:', isAuthenticated);
    // console.log('🏷️ Current root category:', currentRootCategory);
    // console.log('🏷️ props.config:', props.config);

    const productSignInLabel = props.config.productSignInLabel || 'Achetez';
    /**
     * Retourne la classe CSS de la root category en minuscules.
     */
    const getRootCategoryClass = (currentRootCategory: string) =>
        currentRootCategory ? `${currentRootCategory.toLowerCase()}-results` : '';

    // console.log('🏷️ getRootCategoryClass:', getRootCategoryClass(currentRootCategory));

    const [businessPartnerId, setBusinessPartnerId] = useState<string | undefined>(undefined);
    React.useEffect(() => {
        if (isAuthenticated) {
            getBusinessPartnerId(props.context.actionContext).then(id => {
                //console.log('BusinessPartnerId:', id);
                setBusinessPartnerId(id);
            });
        } else {
            setBusinessPartnerId(undefined);
        }
    }, [isAuthenticated, props.context.actionContext]);

    const [customer, setCustomer] = React.useState<any>(null);
    React.useEffect(() => {
        if (isAuthenticated) {
            const actionContext = props.context.actionContext;
            let customerId = isAuthenticated ? props.context.request.user.customerAccountNumber : undefined;
            if (process.env.NODE_ENV === 'development') {
                console.log('🏷️ Environnement de développement - CustomerID simulé');
                customerId = '7';
            }
            if (customerId) {
                const customerInput = new GetCustomerInput(props.context.request.apiSettings, customerId);

                getCustomer(customerInput, actionContext)
                    .then(data => {
                        setCustomer(data);
                    })
                    .catch(error => {
                        setCustomer(null);
                    });
            }
        }
    }, [isAuthenticated, props.context.actionContext, props.context.request.user.customerAccountNumber]);

    // const paginatedProducts = props.data.listPageState.result?.activeProducts || [];
    // const visibleProducts = filterVisibleProducts(paginatedProducts, appSettings, businessPartnerId);

    // let productCountText: string = '';
    // if (visibleProducts.length && visibleProducts.length !== 0) {
    //     productCountText =
    //         visibleProducts.length !== 1 ? format(props.resources.numberOfProducts, visibleProducts.length) : props.resources.oneProduct;
    // } else {
    //     productCountText = format(props.resources.numberOfProducts, 0);
    // }

    if (isMobile) {
        return (
            <React.Fragment>
                {renderCategoryBanner(props)}
                <div className={`container ${getRootCategoryClass(currentRootCategory)}`}>
                    <Module {...SearchResultContainer}>
                        {similarLookProduct ? null : (
                            <Node {...CategoryNavContainer}>
                                {renderCategoryHierarchy(categoryHierarchy)}
                                {/* {renderTitle(TitleViewProps)} */}
                                {renderTitleCount(TitleViewProps, currentRootCategory)}
                                {/* {renderTitleCount({
                                    ...TitleViewProps,
                                    title: {
                                        ...TitleViewProps.title,
                                        titleCount: <span className='ms-search-result__collection-title-count'>{productCountText}</span>
                                    }
                                })} */}
                            </Node>
                        )}
                        {choiceSummary}
                        <Node className='msc-sort-filter-wrapper'>{modalToggle}</Node>
                        {createSearchResultModal(
                            currentRootCategory,
                            isAuthenticated,
                            props.config.titleSpecialNeedsFilter || 'Special needs filter',
                            searchResultModal,
                            refineMenu,
                            sortByOptions,
                            isRecoSearchPage,
                            appSettings
                        )}
                        <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                        {errorMessage}
                        {props.config.enableProdutDescription
                            ? renderProducts(productSignInLabel, currentRootCategory, isAuthenticated, props, businessPartnerId, customer)
                            : renderProductCards(
                                  productSignInLabel,
                                  currentRootCategory,
                                  isAuthenticated,
                                  props,
                                  businessPartnerId,
                                  customer
                              )}
                        {pagination}
                    </Module>
                </div>
            </React.Fragment>
        );
    }
    return (
        <React.Fragment>
            {renderCategoryBanner(props)}
            <div className={`container wrapper-category-items-list ${getRootCategoryClass(currentRootCategory)}`}>
                <Module {...SearchResultContainer}>
                    {similarLookProduct ? null : (
                        <Node {...CategoryNavContainer}>
                            {categoryHierarchy && renderCategoryHierarchy(categoryHierarchy)}
                            {/* {TitleViewProps && renderTitle(TitleViewProps)} */}
                            {renderTitleCount(TitleViewProps, currentRootCategory)}
                            {/* {TitleViewProps &&
                                renderTitleCount({
                                    ...TitleViewProps,
                                    title: {
                                        ...TitleViewProps.title,
                                        titleCount: <span className='ms-search-result__collection-title-count'>{productCountText}</span>
                                    }
                                })} */}
                        </Node>
                    )}
                    <Node {...FeatureSearchContainer}>{similarLookProduct}</Node>
                    <Node {...RefineAndProductSectionContainer}>
                        <Node ref={wrapperRefiner} className='ms-refiner-heading'>
                            <Node className='ms-refiner-heading_title'>
                                <Button
                                    className={isExpanded ? 'ms-refine-filter__toggle_expanded' : 'ms-refine-filter__toggle_collapsed'}
                                    aria-label='Filter'
                                    onClick={toggle}
                                    aria-expanded={isExpanded}
                                    tabIndex={0}
                                >
                                    {resources.filterText}
                                </Button>
                                <div className='ms-search-result-wrapper-title-choice-summary'>{choiceSummary}</div>
                                <div className='ms-search-result-wrapper-sort-by-category'>
                                    {sortByOptions && !isRecoSearchPage && renderSort(sortByOptions)}
                                </div>
                            </Node>
                            {refineMenu &&
                                isExpanded &&
                                renderRefiner(props.config.titleSpecialNeedsFilter || 'Special needs filter', refineMenu, appSettings)}
                            <Node className='ms-refine__footer'>
                                {refineMenu && isExpanded && renderRefinerFooter(refineMenu, toggle, resources)}
                            </Node>
                        </Node>

                        <Node {...ProductSectionContainer}>
                            {errorMessage}
                            {props.config.enableProdutDescription
                                ? renderProducts(
                                      productSignInLabel,
                                      currentRootCategory,
                                      isAuthenticated,
                                      props,
                                      businessPartnerId,
                                      customer
                                  )
                                : renderProductCards(
                                      productSignInLabel,
                                      currentRootCategory,
                                      isAuthenticated,
                                      props,
                                      businessPartnerId,
                                      customer
                                  )}
                            {pagination}
                        </Node>
                    </Node>
                </Module>
            </div>
        </React.Fragment>
    );
};

/**
 * Render Search result Modal.
 * @params currentRootCategory - The current root category.
 * @param isAuthenticated - The authentication status.
 * @param titleSpecialNeedsFilter - The title for special needs filter.
 * @param modalProps - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const createSearchResultModal = (
    currentRootCategory: string = RootCategoryNames.PRODUCTS_FR,
    isAuthenticated: boolean = false,
    titleSpecialNeedsFilter: string,
    modalProps: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string,
    appSettings?: IAppSettings
): JSX.Element => {
    return React.cloneElement(
        modalProps.modal,
        {},
        modalProps.modalHeader,
        createModalBody(
            currentRootCategory,
            isAuthenticated,
            titleSpecialNeedsFilter,
            modalProps,
            refineMenu,
            sortByDropDown,
            isRecoSearchPage,
            appSettings
        ),
        modalProps.modalFooter
    );
};

/**
 * Render Refiner Mobile.
 * @param props - The Refine Menu view props.
 * @param currentRootCategory - The current root category.
 * @param isAuthenticated - The authentication status.
 * @param titleSpecialNeedsFilter - The title for special needs filter.
 * @param appSettings - Les paramètres d'application pour accéder aux noms techniques.
 * @returns The JSX Element.
 */
const renderRefinerMobile = (
    currentRootCategory: string = RootCategoryNames.PRODUCTS_FR,
    isAuthenticated: boolean = false,
    titleSpecialNeedsFilter: string,
    props: IRefineMenuViewProps,
    appSettings?: IAppSettings
): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        // Traitement uniquement si on peut récupérer le contexte
        let processedRefiners = refiners;

        if (appSettings) {
            processedRefiners = processRefinersWithSpecialNeedsGrouping(titleSpecialNeedsFilter, refiners, appSettings);
        }

        return (
            <Node {...RefinerSectionContainer} className={classNames(RefinerSectionContainer.className, currentRootCategory)}>
                <Node {...RefineMenuContainer}>
                    {processedRefiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Search result Body.
 * @param currentRootCategory - The current root category.
 * @param isAuthenticated - The authentication status.
 * @param titleSpecialNeedsFilter - The title for special needs filter.
 * @param props - The Search Result Modal view props.
 * @param refineMenu - The Refine Menu view props.
 * @param sortByDropDown - The Sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @param appSettings - Les paramètres d'application pour accéder aux noms techniques.
 * @returns The JSX Element.
 */
const createModalBody = (
    currentRootCategory: string,
    isAuthenticated: boolean,
    titleSpecialNeedsFilter: string,
    props: ISearchResultModalViewProps,
    refineMenu: IRefineMenuViewProps,
    sortByDropDown: ISortByViewProps,
    isRecoSearchPage?: string,
    appSettings?: IAppSettings
): JSX.Element | null => {
    if (sortByDropDown) {
        return React.cloneElement(
            props.modalBody,
            {},
            renderSort(sortByDropDown, isRecoSearchPage),
            renderRefinerMobile(currentRootCategory, isAuthenticated, titleSpecialNeedsFilter, refineMenu, appSettings)
        );
    }
    return null;
};

/**
 * Render Refiner.
 * @param titleSpecialNeedsFilter - The title for special needs filter.
 * @param props - The Refine Menu view props.
 * @param appSettings - Les paramètres d'application pour accéder aux noms techniques.
 * @returns The JSX Element.
 */
const renderRefiner = (titleSpecialNeedsFilter: string, props: IRefineMenuViewProps, appSettings?: IAppSettings): JSX.Element | null => {
    const { refiners, RefineMenuContainer, RefinerSectionContainer } = props;
    if (refiners) {
        // Traitement uniquement si on peut récupérer le contexte
        let processedRefiners = refiners;

        if (appSettings) {
            processedRefiners = processRefinersWithSpecialNeedsGrouping(titleSpecialNeedsFilter, refiners, appSettings);
        }

        return (
            <Node {...RefinerSectionContainer}>
                <Node {...RefineMenuContainer}>
                    {processedRefiners.map((submenu, index) => (
                        <React.Fragment key={index}>{submenu}</React.Fragment>
                    ))}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render Refiner Footer.
 * @param props - The Refine Menu view props.
 * @param handler - The Refine menu toggle function.
 * @param resources - Resources to get the done text and aria label.
 * @returns The JSX Element.
 */
const renderRefinerFooter = (
    props: IRefineMenuViewProps,
    handler: MouseEventHandler,
    resources: ISearchResultContainerResources
): JSX.Element | null => {
    const { refiners } = props;
    if (refiners) {
        return (
            <Node className='ms-refine__footer__done'>
                <Node tag='button' className='ms-refine-filter-done' aria-label={resources.doneAriaLabel} tabIndex={0} onClick={handler}>
                    {resources.doneText}
                </Node>
            </Node>
        );
    }
    return null;
};

/**
 * Render sort by drop down.
 * @param props - The sort by view props.
 * @param isRecoSearchPage - The recomandation search page flag.
 * @returns The JSX Element.
 */
const renderSort = (props: ISortByViewProps, isRecoSearchPage?: string): JSX.Element | null => {
    const { SortingContainer, sortByDropDown } = props;
    if (sortByDropDown && !isRecoSearchPage) {
        return <Node {...SortingContainer}>{sortByDropDown}</Node>;
    }
    return null;
};

/**
 * Render Title.
 * @param props - The title view props.
 * @returns The JSX Element.
 */
// const renderTitle = (props: ITitleViewProps): JSX.Element | null => {
//     const { title, TitleContainer } = props;
//     if (title) {
//         return (
//             <Node {...TitleContainer}>
//                 <Node tag='h1' className=''>
//                     {title.titlePrefix}
//                     {title.titleText}
//                 </Node>
//             </Node>
//         );
//     }
//     return null;
// };

export default SearchResultContainerView;
